import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { 
  CheckCircle2, 
  GraduationCap, 
  ArrowLeft, 
  User, 
  Users, 
  BookOpen, 
  Building2,
  FileText,
  Upload,
  AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ScholarshipApplicationPage = () => {
  const [options, setOptions] = useState({
    nukhs: []
  });
  
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    fatherName: '',
    motherName: '',
    nukh: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    // Family Information
    familyIncome: '',
    familyMembers: '',
    fatherOccupation: '',
    // Academic Information
    currentCourse: '',
    courseDuration: '',
    instituteName: '',
    instituteAddress: '',
    instituteCity: '',
    instituteState: '',
    currentYear: '',
    previousMarks: '',
    admissionYear: '',
    // Bank Details (Institution)
    instituteBankName: '',
    instituteBankAccount: '',
    instituteBankIfsc: '',
    tuitionFeeAmount: '',
    // Declaration
    declaration: false,
    otherScholarship: false,
    otherScholarshipDetails: '',
  });

  const [files, setFiles] = useState({
    photoIdProof: null,
    passportPhoto: null,
    marksheet: null,
    incomeProof: null,
    admissionLetter: null,
    courseProsectus: null,
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch dropdown options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await axios.get(`${API}/options`);
        setOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    if (fileList && fileList[0]) {
      // Check file size (max 5MB)
      if (fileList[0].size > 5 * 1024 * 1024) {
        toast.error('फ़ाइल का आकार 5MB से कम होना चाहिए');
        return;
      }
      setFiles({ ...files, [name]: fileList[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.declaration) {
      toast.error('कृपया घोषणा पर सहमति दें');
      return;
    }

    // Validate marks
    const marks = parseFloat(formData.previousMarks);
    if (isNaN(marks) || marks < 60) {
      toast.error('न्यूनतम 60% अंक आवश्यक हैं');
      return;
    }

    // Validate income
    const income = parseFloat(formData.familyIncome);
    if (isNaN(income) || income > 500000) {
      toast.error('वार्षिक पारिवारिक आय ₹5 लाख से अधिक नहीं होनी चाहिए');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Append all text fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Append files
      Object.keys(files).forEach(key => {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      });

      await axios.post(`${API}/scholarship/application`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('आवेदन सफलतापूर्वक जमा हो गया!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'आवेदन जमा करने में विफल। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Card className="p-12 shadow-floating">
            <div className="mb-6">
              <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-4" />
              <h1 className="font-serif font-bold text-3xl text-primary mb-4">
                आवेदन सफलतापूर्वक जमा! / Application Submitted Successfully!
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                महासभा नीड कम मैरिट लोन स्कॉलरशिप के लिए आपका आवेदन प्राप्त हो गया है।
                <br />
                <span className="text-sm">Your application for Mahasabha Need-cum-Merit Loan Scholarship has been received.</span>
              </p>
              <div className="bg-muted p-6 rounded-lg mb-6 text-left">
                <p className="text-sm text-foreground mb-2">
                  <strong>आगे की प्रक्रिया / Next Steps:</strong>
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• आपके आवेदन की समिति द्वारा जांच की जाएगी / Your application will be reviewed by the committee</li>
                  <li>• योग्यता और आवश्यकता के आधार पर मेरिट सूची तैयार की जाएगी / Merit list will be prepared based on eligibility</li>
                  <li>• अंतिम अनुमोदन महासभा अध्यक्ष और सचिव द्वारा दिया जाएगा / Final approval by Mahasabha President and Secretary</li>
                  <li>• चयनित होने पर छात्रवृत्ति राशि सीधे संस्थान के खाते में हस्तांतरित होगी / Scholarship amount will be transferred directly to institution</li>
                  <li>• किसी भी प्रश्न के लिए / For any queries: brahmkhatrimahasabha@gmail.com</li>
                </ul>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>महत्वपूर्ण / Important:</strong> कृपया ऑनलाइन फॉर्म की हार्ड कॉपी सभी संलग्नकों के साथ, विद्यार्थी और अभिभावक के हस्ताक्षर सहित महासभा को जमा करें।
                  <br />
                  <span className="text-xs">Please submit a hard copy of the online form with all attachments, signed by student and guardian, to Mahasabha.</span>
                </p>
              </div>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => (window.location.href = '/')}
                  className="bg-primary hover:bg-primary/90"
                >
                  होम पेज पर जाएं / Go to Home
                </Button>
                <Link to="/scholarships">
                  <Button variant="outline">
                    छात्रवृत्ति पेज देखें / View Scholarship Page
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/scholarships" className="inline-flex items-center text-primary hover:text-primary/80 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          छात्रवृत्ति पेज पर वापस जाएं / Back to Scholarship Page
        </Link>

        <Card className="p-8 md:p-12 shadow-floating">
          {/* Header */}
          <div className="text-center mb-12 border-b-2 border-primary pb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-full mb-4">
              <GraduationCap className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-primary mb-3">
              महासभा नीड कम मैरिट लोन स्कॉलरशिप
            </h1>
            <h2 className="font-serif font-bold text-xl md:text-2xl text-secondary mb-2">
              छात्रवृत्ति आवेदन फॉर्म / Scholarship Application Form
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन द्वारा उच्च शिक्षा के लिए छात्रवृत्ति
              <br />
              <span className="text-sm">Scholarship for Higher Education by Brahmkshatriya Marwadi Khatri Association</span>
            </p>
          </div>

          {/* Eligibility Reminder */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>पात्रता / Eligibility:</strong> मारवाड़ी ब्रह्मक्षत्रिय समाज का सदस्य (Member of Marwadi Brahmkshatriya Samaj) | न्यूनतम 60% अंक (Min 60% marks) | वार्षिक आय ₹5 लाख या कम (Annual income ≤₹5 Lakh) | एक परिवार से एक छात्र (One student per family)
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2 flex items-center gap-2">
                <User className="w-5 h-5" />
                व्यक्तिगत जानकारी / Personal Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fullName">
                    पूरा नाम / Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    placeholder="विद्यार्थी का पूरा नाम / Student's Full Name"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="nukh">
                    नुख / Nukh <span className="text-destructive">*</span>
                  </Label>
                  <SearchableSelect
                    options={options.nukhs || []}
                    value={formData.nukh}
                    onChange={(value) => setFormData(prev => ({ ...prev, nukh: value }))}
                    placeholder="नुख खोजें / Type to search nukh..."
                    filterMode="startsWith"
                    allowCustom={false}
                    name="nukh"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gender">
                    लिंग / Gender <span className="text-destructive">*</span>
                  </Label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  >
                    <option value="">चुनें / Select</option>
                    <option value="male">पुरुष / Male</option>
                    <option value="female">महिला / Female</option>
                    <option value="other">अन्य / Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fatherName">
                    पिता का नाम / Father's Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="fatherName"
                    name="fatherName"
                    type="text"
                    placeholder="पिता का नाम / Father's Name"
                    value={formData.fatherName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="motherName">
                    माता का नाम / Mother's Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="motherName"
                    name="motherName"
                    type="text"
                    placeholder="माता का नाम / Mother's Name"
                    value={formData.motherName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dateOfBirth">
                    जन्म तिथि / Date of Birth <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">
                    मोबाइल नंबर / Mobile Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="10 अंकों का मोबाइल नंबर / 10 digit mobile"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">
                    ईमेल पता / Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="example@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp">WhatsApp नंबर / WhatsApp Number</Label>
                  <Input
                    id="whatsapp"
                    name="whatsapp"
                    type="tel"
                    placeholder="WhatsApp नंबर (यदि अलग हो) / If different"
                    value={formData.whatsapp}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">
                  पूरा पता / Full Address <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="address"
                  name="address"
                  rows={2}
                  placeholder="मकान नं., गली, मोहल्ला / House No., Street, Area"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">
                    शहर / City <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="शहर / City"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="state">
                    राज्य / State <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="state"
                    name="state"
                    type="text"
                    placeholder="राज्य / State"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="pincode">
                    पिनकोड / Pincode <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="pincode"
                    name="pincode"
                    type="text"
                    placeholder="पिनकोड / Pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Family Information */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2 flex items-center gap-2">
                <Users className="w-5 h-5" />
                पारिवारिक जानकारी / Family Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="familyIncome">
                    वार्षिक पारिवारिक आय (₹) / Annual Family Income <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="familyIncome"
                    name="familyIncome"
                    type="number"
                    placeholder="जैसे: 300000 / e.g., 300000"
                    value={formData.familyIncome}
                    onChange={handleChange}
                    required
                    max="500000"
                  />
                  <p className="text-xs text-muted-foreground mt-1">अधिकतम / Maximum ₹5,00,000</p>
                </div>

                <div>
                  <Label htmlFor="familyMembers">
                    परिवार में सदस्यों की संख्या / Family Members <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="familyMembers"
                    name="familyMembers"
                    type="number"
                    placeholder="जैसे: 4 / e.g., 4"
                    value={formData.familyMembers}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="fatherOccupation">
                    पिता का व्यवसाय / Father's Occupation <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="fatherOccupation"
                    name="fatherOccupation"
                    type="text"
                    placeholder="व्यवसाय / Occupation"
                    value={formData.fatherOccupation}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2 flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                शैक्षणिक जानकारी / Academic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="currentCourse">
                    वर्तमान पाठ्यक्रम / Current Course <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="currentCourse"
                    name="currentCourse"
                    type="text"
                    placeholder="जैसे: B.Tech, MBBS, MBA"
                    value={formData.currentCourse}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="courseDuration">
                    पाठ्यक्रम की अवधि / Course Duration <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="courseDuration"
                    name="courseDuration"
                    type="text"
                    placeholder="जैसे: 4 वर्ष / e.g., 4 years"
                    value={formData.courseDuration}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="instituteName">
                  संस्थान का नाम / Institute Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="instituteName"
                  name="instituteName"
                  type="text"
                  placeholder="कॉलेज/विश्वविद्यालय का पूरा नाम / Full name of College/University"
                  value={formData.instituteName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="instituteAddress">
                  संस्थान का पता / Institute Address <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="instituteAddress"
                  name="instituteAddress"
                  rows={2}
                  placeholder="संस्थान का पूरा पता / Full address of institution"
                  value={formData.instituteAddress}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instituteCity">
                    संस्थान का शहर / Institute City <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="instituteCity"
                    name="instituteCity"
                    type="text"
                    placeholder="शहर / City"
                    value={formData.instituteCity}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="instituteState">
                    संस्थान का राज्य / Institute State <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="instituteState"
                    name="instituteState"
                    type="text"
                    placeholder="राज्य / State"
                    value={formData.instituteState}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="currentYear">
                    वर्तमान वर्ष/सेमेस्टर / Current Year/Semester <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="currentYear"
                    name="currentYear"
                    type="text"
                    placeholder="जैसे: 2nd Year / e.g., 2nd Year"
                    value={formData.currentYear}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="previousMarks">
                    पिछली परीक्षा में अंक (%) / Previous Exam Marks (%) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="previousMarks"
                    name="previousMarks"
                    type="number"
                    placeholder="जैसे: 75 / e.g., 75"
                    value={formData.previousMarks}
                    onChange={handleChange}
                    required
                    min="60"
                    max="100"
                    step="0.01"
                  />
                  <p className="text-xs text-muted-foreground mt-1">न्यूनतम / Minimum 60%</p>
                </div>

                <div>
                  <Label htmlFor="admissionYear">
                    प्रवेश वर्ष / Admission Year <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="admissionYear"
                    name="admissionYear"
                    type="text"
                    placeholder="जैसे: 2024 / e.g., 2024"
                    value={formData.admissionYear}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Institute Bank Details */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                संस्थान के बैंक विवरण / Institute Bank Details
              </h3>
              <p className="text-sm text-muted-foreground">
                छात्रवृत्ति राशि सीधे संस्थान के खाते में हस्तांतरित की जाएगी
                <br />
                <span className="text-xs">Scholarship amount will be transferred directly to the institution's account</span>
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instituteBankName">
                    बैंक का नाम / Bank Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="instituteBankName"
                    name="instituteBankName"
                    type="text"
                    placeholder="बैंक का नाम / Bank Name"
                    value={formData.instituteBankName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="instituteBankAccount">
                    खाता संख्या / Account Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="instituteBankAccount"
                    name="instituteBankAccount"
                    type="text"
                    placeholder="खाता संख्या / Account Number"
                    value={formData.instituteBankAccount}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instituteBankIfsc">
                    IFSC कोड / IFSC Code <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="instituteBankIfsc"
                    name="instituteBankIfsc"
                    type="text"
                    placeholder="IFSC कोड / IFSC Code"
                    value={formData.instituteBankIfsc}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="tuitionFeeAmount">
                    ट्यूशन फीस राशि (₹) / Tuition Fee Amount <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="tuitionFeeAmount"
                    name="tuitionFeeAmount"
                    type="number"
                    placeholder="वार्षिक ट्यूशन फीस / Annual Tuition Fee"
                    value={formData.tuitionFeeAmount}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Document Upload */}
            <div className="space-y-4">
              <h3 className="font-serif font-bold text-xl text-primary border-b pb-2 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                दस्तावेज़ अपलोड करें / Upload Documents
              </h3>
              <p className="text-sm text-muted-foreground">
                सभी दस्तावेज़ PDF या Image (JPG, PNG) प्रारूप में अपलोड करें। अधिकतम आकार: 5MB प्रति फ़ाइल
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border-2 border-dashed rounded-lg">
                  <Label htmlFor="photoIdProof" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-4 h-4" />
                      पहचान प्रमाण (आधार कार्ड) / ID Proof (Aadhar Card)
                    </div>
                  </Label>
                  <Input
                    id="photoIdProof"
                    name="photoIdProof"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="text-sm"
                  />
                  {files.photoIdProof && (
                    <p className="text-xs text-green-600 mt-1">✓ {files.photoIdProof.name}</p>
                  )}
                </div>

                <div className="p-4 border-2 border-dashed rounded-lg">
                  <Label htmlFor="passportPhoto" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-4 h-4" />
                      पासपोर्ट साइज फोटो / Passport Size Photo
                    </div>
                  </Label>
                  <Input
                    id="passportPhoto"
                    name="passportPhoto"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="text-sm"
                  />
                  {files.passportPhoto && (
                    <p className="text-xs text-green-600 mt-1">✓ {files.passportPhoto.name}</p>
                  )}
                </div>

                <div className="p-4 border-2 border-dashed rounded-lg">
                  <Label htmlFor="marksheet" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-4 h-4" />
                      पिछली परीक्षा की अंकतालिका / Previous Exam Marksheet <span className="text-destructive">*</span>
                    </div>
                  </Label>
                  <Input
                    id="marksheet"
                    name="marksheet"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="text-sm"
                  />
                  {files.marksheet && (
                    <p className="text-xs text-green-600 mt-1">✓ {files.marksheet.name}</p>
                  )}
                </div>

                <div className="p-4 border-2 border-dashed rounded-lg">
                  <Label htmlFor="incomeProof" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-4 h-4" />
                      आय प्रमाण पत्र / Income Certificate <span className="text-destructive">*</span>
                    </div>
                  </Label>
                  <Input
                    id="incomeProof"
                    name="incomeProof"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="text-sm"
                  />
                  {files.incomeProof && (
                    <p className="text-xs text-green-600 mt-1">✓ {files.incomeProof.name}</p>
                  )}
                </div>

                <div className="p-4 border-2 border-dashed rounded-lg">
                  <Label htmlFor="admissionLetter" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-4 h-4" />
                      प्रवेश पत्र / Admission Letter / Fee Receipt
                    </div>
                  </Label>
                  <Input
                    id="admissionLetter"
                    name="admissionLetter"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="text-sm"
                  />
                  {files.admissionLetter && (
                    <p className="text-xs text-green-600 mt-1">✓ {files.admissionLetter.name}</p>
                  )}
                </div>

                <div className="p-4 border-2 border-dashed rounded-lg">
                  <Label htmlFor="courseProsectus" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-4 h-4" />
                      पाठ्यक्रम विवरण / Course Prospectus
                    </div>
                  </Label>
                  <Input
                    id="courseProsectus"
                    name="courseProsectus"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="text-sm"
                  />
                  {files.courseProsectus && (
                    <p className="text-xs text-green-600 mt-1">✓ {files.courseProsectus.name}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Other Scholarship */}
            <div className="space-y-4 bg-muted/50 p-6 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="otherScholarship"
                  checked={formData.otherScholarship}
                  onChange={handleChange}
                  className="w-5 h-5 mt-0.5"
                />
                <span className="text-foreground">
                  क्या आप इसी शैक्षणिक सत्र के लिए किसी अन्य संस्था से छात्रवृत्ति प्राप्त कर रहे हैं?
                  <br />
                  <span className="text-sm text-muted-foreground">Are you receiving scholarship from any other institution for this academic year?</span>
                </span>
              </label>

              {formData.otherScholarship && (
                <div className="ml-8">
                  <Label htmlFor="otherScholarshipDetails">विवरण दें / Provide Details:</Label>
                  <Textarea
                    id="otherScholarshipDetails"
                    name="otherScholarshipDetails"
                    rows={2}
                    placeholder="छात्रवृत्ति का नाम और राशि / Scholarship name and amount"
                    value={formData.otherScholarshipDetails}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>

            {/* Declaration */}
            <div className="bg-primary/5 p-6 rounded-lg border-2 border-primary/20">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={handleChange}
                  className="w-5 h-5 mt-0.5"
                  required
                />
                <div className="text-sm text-foreground leading-relaxed">
                  <strong>घोषणा / Declaration:</strong> मैं प्रमाणित करता/करती हूं कि / I hereby certify that:
                  <ul className="mt-2 space-y-1 ml-4 list-disc">
                    <li>उपरोक्त सभी जानकारी सत्य और सही है / All information provided above is true and correct</li>
                    <li>मैं मारवाड़ी ब्रह्मक्षत्रिय समाज का सदस्य हूं / I am a member of Marwadi Brahmkshatriya Samaj</li>
                    <li>यह छात्रवृत्ति ब्याज-मुक्त ऋण की प्रकृति की है / This scholarship is in the nature of an interest-free loan</li>
                    <li>रोजगार प्राप्त करने के बाद मैं इसे वापस करूंगा/करूंगी / I will repay it after getting employment</li>
                    <li>किसी भी गलत जानकारी पर मेरा आवेदन रद्द किया जा सकता है / My application can be cancelled for any false information</li>
                  </ul>
                </div>
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-6">
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/90 text-white font-serif text-lg px-12 py-6 w-full md:w-auto"
                disabled={loading || !formData.declaration}
              >
                {loading ? 'जमा हो रहा है... / Submitting...' : 'आवेदन जमा करें / Submit Application'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ScholarshipApplicationPage;
